import React, { useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, Box, Button, Breadcrumbs, Link, Alert, Chip } from '@mui/material';
import { ArrowBack, Home, Refresh } from '@mui/icons-material';
import { EntityDetail } from '../components/EntityDetail';
import { LoadingSkeleton } from '../components/shared/LoadingSkeleton';
import { useEntity, useEntityTypeConfig, useMenuConfig } from '@/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { getEntityAction } from '@/actions/entityActionRegistry';
import type { EntityActionConfig } from '@/types/Entity';
import { useNotifications } from '@/stores/useNotificationStore';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { isUiConfigEntityPageDisabled, isReadOnlyEntityAction } from '@/utils/entityTypeConfig';
import { useEntityActionDialogs } from '@/components/entities/EntityActionDialogProvider';
import { useIeKitIdPrefix } from '@/hooks/useIeKitLabels';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { logger } from '@equip/utils/logger';
import { useWriteGuard } from '@/hooks/useWriteGuard';

const log = logger.child({ component: 'EntityDetailPage' });

export const EntityDetailPage: React.FC = () => {
  const { entityType, entityId } = useParams<{ entityType: string; entityId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnSearch = (location.state as { returnSearch?: string } | null)?.returnSearch;
  const listPath = `/${entityType}${returnSearch ? `?${returnSearch}` : ''}`;
  const isViewer = useWriteGuard();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { formatIdentifier: formatIeKitId } = useIeKitIdPrefix();
  const person = usePersonLabels('front_window');

  const {
    data: config,
    isLoading: configLoading,
    isFetching: configFetching,
  } = useEntityTypeConfig(entityType!);
  const { data: uiNav } = useMenuConfig();
  const {
    data: entity,
    isLoading: entityLoading,
    error,
    refetch,
  } = useEntity(entityId!, entityType!);
  const { openDialog, closeDialog } = useEntityActionDialogs();

  const detailActions = useMemo(() => {
    if (!config?.ui_configuration?.actions) return [];
    return config.ui_configuration.actions.filter((action) => {
      if (action.scope !== 'detail') return false;
      return Boolean(getEntityAction(action.handler));
    });
  }, [config]);

  const actionContext = useMemo(
    () =>
      entity && config
        ? {
            entity,
            entityTypeConfig: config,
            queryClient,
            notifications,
            navigate,
            openDialog,
            closeDialog,
          }
        : null,
    [entity, config, queryClient, notifications, navigate, openDialog, closeDialog],
  );

  const handleActionClick = useCallback(
    async (action: EntityActionConfig) => {
      if (!actionContext) return;
      const handler = getEntityAction(action.handler);
      if (!handler) return;

      try {
        await handler(actionContext, action);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unexpected error';
        notifications?.error('Action failed', message);
        log.error('Action handler failed', { handler: action.handler, error: String(err) });
      }
    },
    [actionContext, notifications],
  );

  const isEntityPageDisabled = isUiConfigEntityPageDisabled(config?.ui_configuration);

  if (configLoading || configFetching || entityLoading || !config) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (isEntityPageDisabled) {
    return <NotFoundPage />;
  }

  if (error || !entity || !actionContext) {
    return (
      <Container maxWidth={false} sx={{ mt: 2 }}>
        <Alert severity="error">
          {error ? 'Failed to load entity' : `Entity "${entityId}" not found`}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          {(() => {
            const parts: React.ReactNode[] = [];
            parts.push(
              <Link
                key="home"
                color="inherit"
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                }}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                Home
              </Link>,
            );
            const groupKey = config.ui_configuration?.group;
            const groupName = groupKey ? uiNav?.groups?.[groupKey]?.display_name : undefined;
            if (groupName) {
              parts.push(
                <Typography key="group" color="text.primary">
                  {groupName}
                </Typography>,
              );
            }
            parts.push(
              <Link
                key="type"
                color="inherit"
                href={listPath}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(listPath);
                }}
              >
                {config.display_name}
              </Link>,
            );
            parts.push(
              <Typography key="entity" color="text.primary">
                {entity.display_name}
              </Typography>,
            );
            return parts;
          })()}
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h4" component="h1">
                {entity.display_name}
                {entity.__optimistic && (
                  <Chip label="Saving..." size="small" color="info" sx={{ ml: 1 }} />
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {entityType === 'ie_kit'
                  ? formatIeKitId(entity.entity_identifier)
                  : entityType === 'person'
                    ? entity.entity_identifier?.replace(/^INDV-/, person.individualIdPrefix)
                    : entity.entity_identifier}
              </Typography>
            </Box>
            {entityType !== 'person' && (
              <Chip label={entity.entity_type} color="primary" variant="outlined" />
            )}
            {entityType === 'person' && entity.entity_data && (
              <>
                {entity.entity_data.rank && (
                  <Chip label={String(entity.entity_data.rank)} color="primary" variant="outlined" />
                )}
                {entity.entity_data.unit && (
                  <Chip label={String(entity.entity_data.unit)} color="secondary" variant="outlined" />
                )}
                {entity.entity_data.mos && (
                  <Chip label={String(entity.entity_data.mos)} color="default" variant="outlined" />
                )}
              </>
            )}
            <Chip
              label={entity.status}
              color={entity.status === 'active' ? 'success' : 'warning'}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {detailActions.map((action) => {
              const handler = getEntityAction(action.handler);
              if (isViewer && !isReadOnlyEntityAction(action)) return null;
              return (
                <Button
                  key={action.id}
                  variant="contained"
                  onClick={() => {
                    void handleActionClick(action);
                  }}
                  disabled={!handler}
                  size="small"
                >
                  {action.label}
                </Button>
              );
            })}
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate(listPath)}
            >
              Back to {config.display_name}
            </Button>
            <Button
              startIcon={<Refresh />}
              onClick={() => refetch()}
              variant="outlined"
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      <EntityDetail entityId={entityId!} entityType={entityType!} />
    </Container>
  );
};
